import React, { useContext, useEffect, useRef, useState } from 'react';
import {
	FormImageUploaderNewContext,
	IFormImageUploaderNewContext,
	ILoadFromFilePathOptions,
	IMaxFileSizeMB,
} from '../../formImageUploaderNewState/formImageUploaderNewContext';
import { IDescriptionOfAspectRatio } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { downloadBlob } from 'backend/additionalInformation';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { TabItem } from 'components/Tabs/TabItem';
import { Tabs } from 'components/Tabs/Tabs';
import Text from 'components/Text/Text';
import ModalLayout from 'components/ModalLayout';
import { GifTabPane } from './GifTabPane';
import { ImageTabPane, IImageTabPaneHandle } from './ImageTabPane';
import { VideoTabPane } from './VideoTabPane';
import classNames from 'classnames';
import style from './MediaModal.module.scss';

type TActiveTab = 'gif' | 'image' | 'video';

interface IProps {
	horizontalAspectRatio?: number;
	verticalAspectRatio?: number;
	maxWidth?: number;
	maxHeight?: number;
	maxFileSizeMB: IMaxFileSizeMB;
	noImageIconId?: string;
	loadFromFilePathOptions?: ILoadFromFilePathOptions;
	modalTitleTranslationKey?: string;
	modalSubtitleTranslationKey?: string;
	loadFromVideoFilePathOptions?: ILoadFromFilePathOptions;
	descriptionOfAspectRatio?: IDescriptionOfAspectRatio;
	hasVideo?: boolean;
	setIsVideoActive?: (value: boolean) => void;
}

export const MediaModal: React.FC<IProps> = ({
	horizontalAspectRatio,
	verticalAspectRatio,
	maxWidth,
	maxHeight,
	maxFileSizeMB,
	noImageIconId = 'menu',
	loadFromFilePathOptions,
	modalTitleTranslationKey,
	modalSubtitleTranslationKey,
	loadFromVideoFilePathOptions,
	descriptionOfAspectRatio,
	hasVideo = false,
	setIsVideoActive,
}) => {
	const { t } = useTranslation('form');
	const {
		changeTypeOfModalWindow,
		typeOfModalWindow,
		pendingImage,
		pendingGif,
		pendingVideo,
		setPendingImage,
		setPendingGif,
		setPendingVideo,
		uploadImage,
		uploadVideo,
		keepExistingFile,
		isUploadingVideo,
	} = useContext<IFormImageUploaderNewContext>(FormImageUploaderNewContext);

	const [activeTab, setActiveTab] = useState<TActiveTab>(hasVideo ? 'video' : 'image');
	const imageTabRef = useRef<IImageTabPaneHandle>(null);

	const hasVideoTab = !!maxFileSizeMB.video;
	const hasGifTab = !!maxFileSizeMB.gif;
	const isTabbedLayout = hasVideoTab || hasGifTab;

	// Download existing image/gif blob from s3, then set img src from localhost to avoid tainted canvas exception (edit forms)
	const { response, refetch, loading } = useFetchData<AxiosResponse<Blob>, string>(
		loadFromFilePathOptions?.filePath ? downloadBlob : null,
		loadFromFilePathOptions?.filePath || '',
	);

	const {
		response: videoResponse,
		refetch: refetchVideo,
		loading: videoLoading,
	} = useFetchData<AxiosResponse<Blob>, string>(
		loadFromVideoFilePathOptions?.filePath ? downloadBlob : null,
		loadFromVideoFilePathOptions?.filePath || '',
	);

	useEffect(() => {
		if (response) {
			const urlFromBlob = URL.createObjectURL(response.data);
			const loadedExtension = response.config.url?.split('.').pop() === 'gif' ? 'gif' : 'image';
			if (loadedExtension === 'gif') {
				setPendingGif({ file: null, src: urlFromBlob });
			} else {
				setPendingImage({ file: null, src: urlFromBlob });
			}
			if (!hasVideo) {
				setActiveTab(loadedExtension);
			}
		}
	}, [response]);

	useEffect(() => {
		refetch();
	}, [loadFromFilePathOptions?.filePath]);

	useEffect(() => {
		if (videoResponse) {
			setPendingVideo({ file: null, src: URL.createObjectURL(videoResponse.data) });
			if (hasVideo) {
				setActiveTab('video');
			}
		}
	}, [videoResponse]);

	useEffect(() => {
		refetchVideo();
	}, [loadFromVideoFilePathOptions?.filePath]);

	const closeModal = () => {
		changeTypeOfModalWindow(null);
	};

	const handleApply = () => {
		if (activeTab === 'video') {
			if (pendingVideo.file instanceof File) {
				uploadVideo(pendingVideo.file);
			}
			setIsVideoActive?.(true);
		} else if (activeTab === 'gif') {
			if (pendingGif.file) {
				uploadImage(pendingGif.file);
			} else {
				keepExistingFile();
			}
			setIsVideoActive?.(false);
		} else {
			imageTabRef.current?.apply();
		}
		closeModal();
	};

	const getIsApplyDisabled = () => {
		if (activeTab === 'video') {
			return !pendingVideo.src || isUploadingVideo;
		}
		if (activeTab === 'gif') {
			return !pendingGif.src;
		}
		return !pendingImage.src;
	};
	const isApplyDisabled = getIsApplyDisabled();

	const renderRecommendedSizes = () => {
		if (!descriptionOfAspectRatio) {
			return null;
		}

		return (
			<div className={style.recommendedSizes}>
				<Text translationNameSpace="form" translationKey="recommendedSizesTitle" size="m" weight="bold" />
				<div className={style.recommendedSizesCards}>
					<div className={style.recommendedSizesCard}>
						<Text
							translationNameSpace="form"
							translationKey="horizontalOrientationTitle"
							size="s"
							weight="bold"
						/>
						<div>
							{t('ratio')} <span>{descriptionOfAspectRatio.horizontal.ratio}</span>
						</div>
						<div>
							{t('optimalResolutions')}{' '}
							<span>
								{t('optimalResolutionValues', {
									a: descriptionOfAspectRatio.horizontal.optimalResolutions.a,
									b: descriptionOfAspectRatio.horizontal.optimalResolutions.b,
								})}
							</span>
						</div>
					</div>
					<div className={style.recommendedSizesCard}>
						<Text
							translationNameSpace="form"
							translationKey="verticalOrientationTitle"
							size="s"
							weight="bold"
						/>
						<div>
							{t('ratio')} <span>{descriptionOfAspectRatio.vertical.ratio}</span>
						</div>
						<div>
							{t('optimalResolutions')}{' '}
							<span>
								{t('optimalResolutionValues', {
									a: descriptionOfAspectRatio.vertical.optimalResolutions.a,
									b: descriptionOfAspectRatio.vertical.optimalResolutions.b,
								})}
							</span>
						</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<ModalLayout width="60%" isVisible={typeOfModalWindow === 'cropper'} setIsShowModal={closeModal}>
			<div className={style.content}>
				{modalTitleTranslationKey && (
					<div className={style.header}>
						<Text
							translationNameSpace="form"
							translationKey={modalTitleTranslationKey}
							size="l"
							weight="bold"
						/>
						{modalSubtitleTranslationKey && (
							<Text translationNameSpace="form" translationKey={modalSubtitleTranslationKey} size="m" />
						)}
					</div>
				)}
				{isTabbedLayout ? (
					<>
						<Tabs>
							{hasVideoTab && (
								<TabItem
									label={
										<Text
											translationNameSpace="form"
											translationKey="videoTabLabel"
											asFragment={true}
										/>
									}
									isActive={activeTab === 'video'}
									onClick={() => setActiveTab('video')}
									dataTest="videoTab"
								/>
							)}
							{hasGifTab && (
								<TabItem
									label={
										<Text
											translationNameSpace="form"
											translationKey="gifTabLabel"
											asFragment={true}
										/>
									}
									isActive={activeTab === 'gif'}
									onClick={() => setActiveTab('gif')}
									dataTest="gifTab"
								/>
							)}
							<TabItem
								label={
									<Text
										translationNameSpace="form"
										translationKey="imageTabLabel"
										asFragment={true}
									/>
								}
								isActive={activeTab === 'image'}
								onClick={() => setActiveTab('image')}
								dataTest="imageTab"
							/>
						</Tabs>
						{hasVideoTab && (
							<div className={classNames(style.tabPane, { hidden: activeTab !== 'video' })}>
								<VideoTabPane maxFileSizeMB={maxFileSizeMB} loading={videoLoading} />
							</div>
						)}
						{hasGifTab && (
							<div className={classNames(style.tabPane, { hidden: activeTab !== 'gif' })}>
								<GifTabPane
									maxFileSizeMB={maxFileSizeMB}
									noImageIconId={noImageIconId}
									loading={loading}
								/>
							</div>
						)}
						<div className={classNames(style.tabPane, { hidden: activeTab !== 'image' })}>
							<ImageTabPane
								ref={imageTabRef}
								horizontalAspectRatio={horizontalAspectRatio}
								verticalAspectRatio={verticalAspectRatio}
								maxWidth={maxWidth}
								maxHeight={maxHeight}
								maxFileSizeMB={maxFileSizeMB}
								noImageIconId={noImageIconId}
								loading={loading}
								setIsVideoActive={setIsVideoActive}
							/>
						</div>
					</>
				) : (
					<ImageTabPane
						ref={imageTabRef}
						horizontalAspectRatio={horizontalAspectRatio}
						verticalAspectRatio={verticalAspectRatio}
						maxWidth={maxWidth}
						maxHeight={maxHeight}
						maxFileSizeMB={maxFileSizeMB}
						noImageIconId={noImageIconId}
						loading={loading}
					/>
				)}
				{renderRecommendedSizes()}
				<div className={style.footer}>
					<div data-test="cancelUploadButton">
						<NewButton variant="outline" color="gray" onClick={closeModal}>
							<Text translationNameSpace="form" translationKey="cancelButton" asFragment={true} />
						</NewButton>
					</div>
					<div data-test="applyUploadButton">
						<NewButton variant="primary" color="purple" onClick={handleApply} disabled={isApplyDisabled}>
							<Text translationNameSpace="form" translationKey="applyButton" asFragment={true} />
						</NewButton>
					</div>
				</div>
			</div>
		</ModalLayout>
	);
};
